import { useMemo, useState } from 'react';
import { Check, Sparkles, X } from 'lucide-react';
import type { Account } from '@/lib/api/helpdesk';
import type { CustomerLinks, LinkOrigin, MatchCustomer } from '@/lib/helpdesk/matchAccounts';
import { AccountPickerModal } from './AccountPickerModal';

const BADGES: Record<LinkOrigin, { label: string; className: string }> = {
  saved: { label: 'Saved', className: 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-gray-300' },
  auto: { label: 'Auto-matched', className: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  manual: { label: 'Selected', className: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
};

interface Props {
  customers: MatchCustomer[];
  accounts: Account[];
  links: CustomerLinks;
  onChange: (links: CustomerLinks) => void;
  disabled?: boolean;
  warning?: string;
}

/** Controlled customer-first mapper: one row per dashboard customer, one Zoho account per row. */
export function CustomerAccountMapper({ customers, accounts, links, onChange, disabled = false, warning }: Props) {
  const [picking, setPicking] = useState<MatchCustomer | null>(null);

  const rows = useMemo(
    () => [...customers].sort((a, b) => a.customer_name.localeCompare(b.customer_name)),
    [customers],
  );
  const linked = rows.filter(customer => links[customer.customer_id]);
  const autoCount = linked.filter(customer => links[customer.customer_id].origin === 'auto').length;
  // Who holds each account, so the picker can explain an unavailable one.
  const linkedBy = useMemo(() => Object.fromEntries(rows.flatMap(customer => {
    const held = links[customer.customer_id];
    return held ? [[held.account_id, customer.customer_name] as const] : [];
  })), [rows, links]);

  const pick = (customerId: string, account: Account) => {
    onChange({ ...links, [customerId]: { account_id: account.id, account_name: account.name, origin: 'manual' } });
    setPicking(null);
  };
  const clear = (customerId: string) => {
    const next = { ...links };
    delete next[customerId];
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        {linked.length} of {rows.length} customers linked · {autoCount} auto-matched
      </p>
      {warning && <p role="alert" className="text-sm text-amber-700 dark:text-amber-400">{warning}</p>}
      <ul className="max-h-96 overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg">
        {rows.map(customer => {
          const link = links[customer.customer_id];
          return (
            <li key={customer.customer_id} aria-label={customer.customer_name} className="px-4 py-3">
              <div className="flex items-center justify-between gap-4">
                <span className="font-medium text-gray-900 dark:text-white">{customer.customer_name}</span>
                {link ? (
                  <span className="flex items-center gap-2 text-sm">
                    <span className="text-gray-900 dark:text-white">{link.account_name}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${BADGES[link.origin].className}`}>
                      {link.origin === 'auto'
                        ? <Sparkles className="w-3 h-3 inline mr-1" aria-hidden />
                        : <Check className="w-3 h-3 inline mr-1" aria-hidden />}
                      {BADGES[link.origin].label}
                    </span>
                    <button type="button" onClick={() => clear(customer.customer_id)} disabled={disabled}
                      aria-label={`Unlink ${customer.customer_name}`}
                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-50">
                      <X className="w-4 h-4" aria-hidden />
                    </button>
                  </span>
                ) : (
                  <span className="flex items-center gap-3 text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Not linked</span>
                    <button type="button" disabled={disabled}
                      onClick={() => setPicking(customer)}
                      className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed">
                      Select account…
                    </button>
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {picking && !disabled && (
        <AccountPickerModal
          customerName={picking.customer_name}
          accounts={accounts}
          linkedBy={linkedBy}
          onPick={account => pick(picking.customer_id, account)}
          onClose={() => setPicking(null)}
        />
      )}
    </div>
  );
}
